import { useCallback, useContext } from "react";
import { ReactReduxContext } from "react-redux";
import {
  type HotelAvailabilityTrackingProperties,
  type HotelShoppedProductGroupedTrackingProperties,
  type EventTransformerHooks,
  useEventTracking,
  type GlobalTrackingAttributes,
  type UseInstantTransformEvent,
} from "@b2bportal/core-tracking";
import {
  calculateDaysInAdvance,
  getDaysBetween,
} from "@b2bportal/core-utilities";
import { LodgingCancelScenarioEnum } from "@b2bportal/lodging-cancel-api/lib/api";
import { getRooms } from "../features/shop/store/selectors";
import type { HotelsState } from "../store";
import {
  type HotelCoverViewed,
  HotelEventName,
  type HotelHTSEvent,
  type HotelListLoaded,
  type HotelMapLoaded,
  type HotelRoomInfoListLoaded,
  type HotelRoomSelected,
  type HotelEntry,
  HOTEL_PRODUCT_TYPE,
  type HotelViewedSearch,
  type HotelCancelReservationSuccessViewed,
  type HotelCalendarOpened,
  type HotelCalendarDateChanged,
  type HotelCalendarDatesSelected,
  type HotelGuestsSelected,
  type HotelGuestsSelectionOpened,
  type HotelConfirmCancelReservationButtonClicked,
  type HotelCancelReservationComponentViewed,
  type HotelPriceChangedCheckoutModalOpened,
  HOTEL_PRICE_INCREASED,
  HOTEL_PRICE_DECREASE,
  HotelCommonTrackingEventsName,
  type HotelViewedLodgingInList,
  type HotelRoomDetailsOpened,
  type HotelViewedRoomInList,
  type HotelFilterTapped,
  type HotelMapCardTapped,
  type HotelTappedRedoSearchInMap,
  LodgingShopTrackingEvents,
  type HotelViewedList,
  type HotelSwipedCarousel,
  type HotelUpdatedListPage,
} from "./events";
import {
  getHotelAvailabilityTrackingProperties,
  getHotelShopAvailabilityTrackingProperties,
  getHotelShopTrackingProperties,
  getLodgingTrackingPropertiesByHotelId,
  getSelectedRoomIndexFromRooms,
  getTrackingPropertiesForRoomRate,
} from "./selectors";
import {
  type HotelAppLoadedRawEvent,
  type HotelsRawEvent,
  HotelsRawEventName,
  type HotelAvailabilityPageLoadedRawEvent,
  type HotelAvailabilityPageMapLoadedRawEvent,
  type HotelShopPageRoomSelectedRawEvent,
  type HotelShopPageLoadedRawEvent,
  type HotelShopPageRoomsLoadedRawEvent,
  type HotelSearchLoadedRawEvent,
  type HotelCancelReservationSuccessViewedRawEvent,
  type HotelCalendarOpenedRawEvent,
  type HotelCalendarDateChangedRawEvent,
  type HotelCalendarDatesSelectedRawEvent,
  type HotelGuestsSelectedRawEvent,
  type HotelGuestsSelectionOpenedRawEvent,
  type HotelConfirmCancelReservationButtonClickedRawEvent,
  type HotelCancelReservationComponentViewedRawEvent,
  type HotelPriceChangedCheckoutModalOpenedRawEvent,
  type HotelFilterOpenedRawEvent,
  type HotelMapCardTappedRawEvent,
  type HotelViewedLodgingInListRawEvent,
  type HotelShopPageRoomsDetailsOpenedRawEvent,
  type HotelViewedRoomInListRawEvent,
  type HotelTappedRedoSearchInMapRawEvent,
  type HotelAvailabilityPageListViewedRawEvent,
  type HotelSwipedCarouselRawEvent,
  type HotelUpdatedListPageRawEvent,
} from "./types";

const roundPriceToScale = (price: number) => {
  return Math.round(price * 100) / 100;
};

export const commonProperties = {
  productType: HOTEL_PRODUCT_TYPE,
};

export const defaultAvailabilityPageEventTransformer = <
  T extends HotelHTSEvent,
>(
  eventName: T["name"],
  providers: Providers,
  extraProperties: Omit<
    T["properties"],
    | keyof HotelAvailabilityTrackingProperties["properties"]
    | keyof typeof commonProperties
  >
) => {
  const { properties, encryptedProperties } =
    getHotelAvailabilityTrackingProperties(providers.reduxState);
  return {
    name: eventName,
    properties: {
      ...properties,
      ...extraProperties,
      ...commonProperties,
    },
    encryptedProperties: encryptedProperties ?? [],
  };
};

export const defaultShopPageEventTransformer = <T extends HotelHTSEvent>(
  eventName: T["name"],
  providers: Providers,
  extraProperties: Omit<
    T["properties"],
    | keyof HotelShoppedProductGroupedTrackingProperties
    | keyof typeof commonProperties
  >
): {
  name: T["name"];
  properties: T["properties"];
  encryptedProperties: string[];
} => {
  const {
    properties: shopAvailabilityProperties,
    encryptedProperties: availabilityEncryptedTrackingProperties,
  } = getHotelShopAvailabilityTrackingProperties(providers.reduxState);
  const { properties: shopProperties, encryptedProperties } =
    getHotelShopTrackingProperties(providers.reduxState);

  return {
    name: eventName,
    properties: {
      ...shopAvailabilityProperties,
      ...shopProperties,
      ...commonProperties,
      ...extraProperties,
    },
    encryptedProperties: [
      availabilityEncryptedTrackingProperties ?? "",
      encryptedProperties ?? "",
    ].filter(Boolean),
  };
};

export const trackOnlyOncePerSession = (event: HotelHTSEvent): boolean => {
  const sessionStorageKey = `hotel_event_${event.name}`;
  if (sessionStorage.getItem(sessionStorageKey)) {
    return false;
  }
  sessionStorage.setItem(sessionStorageKey, "true");
  return true;
};

export const useHotelAppLoadedTransformer: UseInstantTransformEvent<
  HotelAppLoadedRawEvent,
  HotelEntry
> = () => {
  return useCallback((event) => {
    const name: HotelCommonTrackingEventsName.hotel_entry =
      HotelCommonTrackingEventsName.hotel_entry;
    const htsEvent = {
      name,
      properties: {
        entry: event.properties.entry,
        ...commonProperties,
      },
    };

    return shouldTrackEvent(htsEvent) ? htsEvent : undefined;
  }, []);
};

export const useHotelSearchLoadedTransformer: UseInstantTransformEvent<
  HotelSearchLoadedRawEvent,
  HotelViewedSearch
> = () => {
  return useCallback(() => {
    const name = HotelEventName.hotel_viewed_search;
    const htsEvent: HotelViewedSearch = {
      name,
      properties: {},
    };

    return shouldTrackEvent(htsEvent) ? htsEvent : undefined;
  }, []);
};

export const useHotelAvailabilityPageLoadedTransformer: UseInstantTransformEvent<
  HotelAvailabilityPageLoadedRawEvent,
  HotelListLoaded
> = () => {
  const getProviders = useGetProviders();

  return useCallback(
    (event) => {
      const htsEvent = defaultAvailabilityPageEventTransformer<HotelListLoaded>(
        HotelEventName.hotel_loaded_list_page,
        getProviders(),
        {
          page_index: event.properties?.pageIndex ?? 0,
          page_property_count: event.properties?.pagePropertyCount ?? 0,
        }
      );

      return shouldTrackEvent(htsEvent) ? htsEvent : undefined;
    },
    [getProviders]
  );
};

export const useHotelAvailabilityPageMapLoadedTransformer: UseInstantTransformEvent<
  HotelAvailabilityPageMapLoadedRawEvent,
  HotelMapLoaded
> = () => {
  const getProviders = useGetProviders();

  return useCallback(() => {
    const htsEvent = defaultAvailabilityPageEventTransformer<HotelMapLoaded>(
      HotelEventName.hotel_viewed_map,
      getProviders(),
      {}
    );

    return shouldTrackEvent(htsEvent) ? htsEvent : undefined;
  }, [getProviders]);
};

export const useHotelAvailabilityPageViewedListTransformer: UseInstantTransformEvent<
  HotelAvailabilityPageListViewedRawEvent,
  HotelViewedList
> = () => {
  const getProviders = useGetProviders();

  return useCallback(() => {
    const htsEvent = defaultAvailabilityPageEventTransformer<HotelViewedList>(
      HotelEventName.hotel_viewed_list,
      getProviders(),
      {}
    );

    return shouldTrackEvent(htsEvent) ? htsEvent : undefined;
  }, [getProviders]);
};

export const useHotelShopPageRoomsLoadedTransformer: UseInstantTransformEvent<
  HotelShopPageRoomsLoadedRawEvent,
  HotelRoomInfoListLoaded
> = () => {
  const getProviders = useGetProviders();

  return useCallback(() => {
    const htsEvent = defaultShopPageEventTransformer<HotelRoomInfoListLoaded>(
      HotelEventName.hotel_loaded_room_info_list,
      getProviders(),
      {}
    );

    return shouldTrackEvent(htsEvent) ? htsEvent : undefined;
  }, [getProviders]);
};

export const useHotelShopPageRoomsDetailsOpenedTransformer: UseInstantTransformEvent<
  HotelShopPageRoomsDetailsOpenedRawEvent,
  HotelRoomDetailsOpened
> = () => {
  const getProviders = useGetProviders();

  return useCallback(() => {
    const htsEvent = defaultShopPageEventTransformer<HotelRoomDetailsOpened>(
      HotelEventName.hotel_tapped_bed_types,
      getProviders(),
      {}
    );

    return shouldTrackEvent(htsEvent) ? htsEvent : undefined;
  }, [getProviders]);
};

export const useHotelShopPageLoadedTransformer: UseInstantTransformEvent<
  HotelShopPageLoadedRawEvent,
  HotelCoverViewed
> = () => {
  const getProviders = useGetProviders();

  return useCallback(() => {
    const htsEvent = defaultShopPageEventTransformer<HotelCoverViewed>(
      HotelEventName.hotel_viewed_cover,
      getProviders(),
      {}
    );

    return shouldTrackEvent(htsEvent) ? htsEvent : undefined;
  }, [getProviders]);
};

export const useHotelShopPageRoomSelectedTransformer: UseInstantTransformEvent<
  HotelShopPageRoomSelectedRawEvent,
  HotelRoomSelected
> = () => {
  const getProviders = useGetProviders();

  return useCallback(
    (event) => {
      const rooms = getRooms(getProviders().reduxState);

      const htsEvent = defaultShopPageEventTransformer<HotelRoomSelected>(
        HotelEventName.hotel_tapped_room_selection_continue,
        getProviders(),
        {
          ...getTrackingPropertiesForRoomRate(
            rooms.data,
            event.properties.rate
          ),
          room_index_in_shop: getSelectedRoomIndexFromRooms(
            event.properties.rate,
            rooms.data
          ),
          room_type_count: rooms.data.length,
          ...event.properties,
        }
      );

      return shouldTrackEvent(htsEvent) ? htsEvent : undefined;
    },
    [getProviders]
  );
};

export const useHotelViewedRoomInListTransformer: UseInstantTransformEvent<
  HotelViewedRoomInListRawEvent,
  HotelViewedRoomInList
> = () => {
  const getProviders = useGetProviders();

  return useCallback(
    (event) => {
      const htsEvent = defaultShopPageEventTransformer<HotelViewedRoomInList>(
        HotelEventName.hotel_viewed_room_in_list,
        getProviders(),
        event.properties
      );

      return shouldTrackEvent(htsEvent) ? htsEvent : undefined;
    },
    [getProviders]
  );
};

export const useHotelGuestsSelectionOpenedTransformer: UseInstantTransformEvent<
  HotelGuestsSelectionOpenedRawEvent,
  HotelGuestsSelectionOpened
> = () => {
  return useCallback((event) => {
    return {
      name: HotelEventName.hotel_tapped_guests,
      properties: {
        adults_count: event.properties.adults,
        children_count: event.properties.childrenAges.length,
      },
    };
  }, []);
};

export const useHotelGuestsSelectedTransformer: UseInstantTransformEvent<
  HotelGuestsSelectedRawEvent,
  HotelGuestsSelected
> = () => {
  return useCallback((event) => {
    return {
      name: HotelEventName.hotel_tapped_apply_guests,
      properties: {
        adults_count: event.properties.adults,
        children_count: event.properties.childrenAges.length,
      },
    };
  }, []);
};

export const useHotelCalendarOpenedTransformer: UseInstantTransformEvent<
  HotelCalendarOpenedRawEvent,
  HotelCalendarOpened
> = () => {
  return useCallback(() => {
    return {
      name: HotelEventName.hotel_viewed_calendar,
      properties: {},
    };
  }, []);
};

const calculateLengthOfStay = (startDate: string, endDate?: string): number => {
  if (!startDate || !endDate) return 0;
  const checkInDate = new Date(startDate);
  const checkOutDate = new Date(endDate);
  return getDaysBetween(checkInDate, checkOutDate);
};

export const useHotelCalendarDateChangedTransformer: UseInstantTransformEvent<
  HotelCalendarDateChangedRawEvent,
  HotelCalendarDateChanged
> = () => {
  return useCallback((event) => {
    const advance = calculateDaysInAdvance(event.properties.startDate);
    return {
      name: HotelEventName.hotel_selected_calendar_date,
      properties: {
        check_in_date: event.properties.startDate,
        check_out_date: event.properties.endDate,
        los: calculateLengthOfStay(
          event.properties.startDate,
          event.properties.endDate
        ),
        is_last_minute: advance === 1,
        advance,
      },
    };
  }, []);
};

export const useHotelCalendarDatesSelectedTransformer: UseInstantTransformEvent<
  HotelCalendarDatesSelectedRawEvent,
  HotelCalendarDatesSelected
> = () => {
  return useCallback((event) => {
    const advance = calculateDaysInAdvance(event.properties.startDate);
    return {
      name: HotelEventName.hotel_tapped_select_dates,
      properties: {
        check_in_date: event.properties.startDate,
        check_out_date: event.properties.endDate,
        los: calculateLengthOfStay(
          event.properties.startDate,
          event.properties.endDate
        ),
        is_last_minute: advance === 1,
        advance,
      },
    };
  }, []);
};

export const useHotelCancelReservationSuccessViewedTransformer: UseInstantTransformEvent<
  HotelCancelReservationSuccessViewedRawEvent,
  HotelCancelReservationSuccessViewed
> = () => {
  return useCallback((event) => {
    return {
      name: HotelEventName.self_serve_cancel_response_success,
      properties: {
        product: HOTEL_PRODUCT_TYPE,
        cancel_scenario: event.properties.cancel_scenario,
        non_cfar:
          event.properties.cancel_scenario !==
          LodgingCancelScenarioEnum.CfarRefundable,
      },
    };
  }, []);
};

export const useHotelCancelReservationComponentViewedTransformer: UseInstantTransformEvent<
  HotelCancelReservationComponentViewedRawEvent,
  HotelCancelReservationComponentViewed
> = () => {
  return useCallback((event) => {
    return {
      name: HotelEventName.self_serve_viewed_cancel_modal,
      properties: {
        cancel_scenario: event.properties.cancel_scenario,
        non_cfar:
          event.properties.cancel_scenario !==
          LodgingCancelScenarioEnum.CfarRefundable,
      },
    };
  }, []);
};

export const useHotelConfirmCancelReservationButtonClickedTransformer: UseInstantTransformEvent<
  HotelConfirmCancelReservationButtonClickedRawEvent,
  HotelConfirmCancelReservationButtonClicked
> = () => {
  return useCallback((event) => {
    return {
      name: HotelEventName.self_serve_click_cancel_button,
      properties: {
        cancel_scenario: event.properties.cancel_scenario,
        non_cfar:
          event.properties.cancel_scenario !==
          LodgingCancelScenarioEnum.CfarRefundable,
        product: HOTEL_PRODUCT_TYPE,
      },
    };
  }, []);
};

export const useHotelPriceChangedCheckoutModalOpenedTransformer: UseInstantTransformEvent<
  HotelPriceChangedCheckoutModalOpenedRawEvent,
  HotelPriceChangedCheckoutModalOpened
> = () => {
  const getProviders = useGetProviders();
  return useCallback(
    (event) => {
      const providers = getProviders();
      const { properties: shopProperties } = getHotelShopTrackingProperties(
        providers.reduxState
      );

      const modalType =
        event.properties.initialPrice.value <
        event.properties.updatedPrice.value
          ? HOTEL_PRICE_INCREASED
          : HOTEL_PRICE_DECREASE;

      return defaultShopPageEventTransformer<HotelPriceChangedCheckoutModalOpened>(
        HotelEventName.modal_alert,
        providers,
        {
          product_type: HOTEL_PRODUCT_TYPE,
          modal_type: modalType,
          price_change: Math.abs(
            roundPriceToScale(
              event.properties.updatedPrice.value -
                event.properties.initialPrice.value
            )
          ),
          country_code: shopProperties.country,
          initial_price: event.properties.initialPrice.value,
          updated_price: event.properties.updatedPrice.value,
        }
      );
    },
    [getProviders]
  );
};

export const useHotelFilterOpenedTransformer: UseInstantTransformEvent<
  HotelFilterOpenedRawEvent,
  HotelFilterTapped
> = () => {
  return useCallback(() => {
    return {
      name: HotelEventName.hotel_tapped_filter,
      properties: {},
    };
  }, []);
};

export const useHotelMapCardTappedTransformer: UseInstantTransformEvent<
  HotelMapCardTappedRawEvent,
  HotelMapCardTapped
> = () => {
  const getProviders = useGetProviders();

  return useCallback(
    (event) => {
      const { properties, encryptedProperties } =
        getLodgingTrackingPropertiesByHotelId(
          getProviders().reduxState,
          event.properties.hotelId
        );
      const htsEvent: HotelMapCardTapped = {
        name: HotelEventName.hotel_tapped_map_card,
        properties,
        encryptedProperties,
      };

      return htsEvent;
    },
    [getProviders]
  );
};

export const useHotelViewedLodgingInListTransformer: UseInstantTransformEvent<
  HotelViewedLodgingInListRawEvent,
  HotelViewedLodgingInList
> = () => {
  const getProviders = useGetProviders();

  return useCallback(
    (event) => {
      const { properties, encryptedProperties } =
        getLodgingTrackingPropertiesByHotelId(
          getProviders().reduxState,
          event.properties.hotelId
        );
      const htsEvent =
        defaultAvailabilityPageEventTransformer<HotelViewedLodgingInList>(
          HotelEventName.hotel_viewed_lodging_in_list,
          getProviders(),
          properties
        );

      return {
        ...htsEvent,
        encryptedProperties,
      };
    },
    [getProviders]
  );
};

export const useHotelTappedRedoSearchInMapTransformer: UseInstantTransformEvent<
  HotelTappedRedoSearchInMapRawEvent,
  HotelTappedRedoSearchInMap
> = () => {
  const getProviders = useGetProviders();

  return useCallback(
    (event) => {
      const htsEvent =
        defaultShopPageEventTransformer<HotelTappedRedoSearchInMap>(
          LodgingShopTrackingEvents.hotel_tapped_redo_search_in_map,
          getProviders(),
          {
            page_index: event["properties"]?.pageIndex,
            page_property_count: event["properties"]?.pagePropertyCount,
          }
        );

      return shouldTrackEvent(htsEvent) ? htsEvent : undefined;
    },
    [getProviders]
  );
};

export const useHotelSwipedCarouselTransformer: UseInstantTransformEvent<
  HotelSwipedCarouselRawEvent,
  HotelSwipedCarousel
> = () => {
  const getProviders = useGetProviders();
  return useCallback(
    (event) => {
      const { properties, encryptedProperties } =
        getLodgingTrackingPropertiesByHotelId(
          getProviders().reduxState,
          event.properties.hotelId
        );
      const htsEvent =
        defaultAvailabilityPageEventTransformer<HotelSwipedCarousel>(
          HotelEventName.hotel_swiped_carousel,
          getProviders(),
          properties
        );

      return {
        ...htsEvent,
        encryptedProperties,
      };
    },
    [getProviders]
  );
};

export const useHotelUpdatedListPageTransformer: UseInstantTransformEvent<
  HotelUpdatedListPageRawEvent,
  HotelUpdatedListPage
> = () => {
  const getProviders = useGetProviders();
  return useCallback(
    (event) => {
      return defaultAvailabilityPageEventTransformer<HotelUpdatedListPage>(
        HotelEventName.hotel_updated_list_page,
        getProviders(),
        {
          page_index: event.properties?.pageIndex ?? 0,
          page_property_count: event.properties?.pagePropertyCount ?? 0,
        }
      );
    },
    [getProviders]
  );
};

export const HOTEL_HTS_EVENT_TRANSFORMER_HOOKS: EventTransformerHooks<
  HotelsRawEvent,
  HotelHTSEvent
> = {
  [HotelsRawEventName.HotelAppLoaded]: [useHotelAppLoadedTransformer],
  [HotelsRawEventName.HotelSearchLoaded]: [useHotelSearchLoadedTransformer],
  [HotelsRawEventName.HotelAvailabilityPageLoaded]: [
    useHotelAvailabilityPageLoadedTransformer,
  ],
  [HotelsRawEventName.HotelAvailabilityPageMapViewed]: [
    useHotelAvailabilityPageMapLoadedTransformer,
  ],
  [HotelsRawEventName.HotelAvailabilityPageListViewed]: [
    useHotelAvailabilityPageViewedListTransformer,
  ],
  [HotelsRawEventName.HotelShopPageRoomsLoaded]: [
    useHotelShopPageRoomsLoadedTransformer,
  ],
  [HotelsRawEventName.HotelShopPageLoaded]: [useHotelShopPageLoadedTransformer],
  [HotelsRawEventName.HotelShopPageRoomSelected]: [
    useHotelShopPageRoomSelectedTransformer,
  ],
  [HotelsRawEventName.HotelCancelReservationSuccessViewed]: [
    useHotelCancelReservationSuccessViewedTransformer,
  ],
  [HotelsRawEventName.HotelConfirmCancelReservationButtonClicked]: [
    useHotelConfirmCancelReservationButtonClickedTransformer,
  ],
  [HotelsRawEventName.HotelCancelReservationComponentViewed]: [
    useHotelCancelReservationComponentViewedTransformer,
  ],
  [HotelsRawEventName.HotelGuestsSelectionOpened]: [
    useHotelGuestsSelectionOpenedTransformer,
  ],
  [HotelsRawEventName.HotelGuestsSelected]: [useHotelGuestsSelectedTransformer],
  [HotelsRawEventName.HotelCalendarOpened]: [useHotelCalendarOpenedTransformer],
  [HotelsRawEventName.HotelCalendarDateChanged]: [
    useHotelCalendarDateChangedTransformer,
  ],
  [HotelsRawEventName.HotelCalendarDatesSelected]: [
    useHotelCalendarDatesSelectedTransformer,
  ],
  [HotelsRawEventName.HotelPriceChangedCheckoutModalOpened]: [
    useHotelPriceChangedCheckoutModalOpenedTransformer,
  ],
  [HotelsRawEventName.HotelShopPageRoomsDetailsOpened]: [
    useHotelShopPageRoomsDetailsOpenedTransformer,
  ],
  [HotelsRawEventName.HotelViewedRoomInList]: [
    useHotelViewedRoomInListTransformer,
  ],
  [HotelsRawEventName.HotelFilterOpened]: [useHotelFilterOpenedTransformer],
  [HotelsRawEventName.HotelMapCardTapped]: [useHotelMapCardTappedTransformer],
  [HotelsRawEventName.HotelViewedLodgingInList]: [
    useHotelViewedLodgingInListTransformer,
  ],
  [HotelsRawEventName.HotelTappedRedoSearchInMap]: [
    useHotelTappedRedoSearchInMapTransformer,
  ],
  [HotelsRawEventName.HotelSwipedCarousel]: [useHotelSwipedCarouselTransformer],
  [HotelsRawEventName.HotelUpdatedListPage]: [
    useHotelUpdatedListPageTransformer,
  ],
};

export const shouldTrackEvent = (event: HotelHTSEvent): boolean => {
  if (event.name === HotelEventName.hotel_entry) {
    return trackOnlyOncePerSession(event);
  }
  return true;
};

export const useGetProviders = () => {
  const { getAllAttributes } = useEventTracking();
  const reduxContext = useContext(ReactReduxContext);

  return useCallback(
    () => ({
      trackingAttributes: getAllAttributes(),
      reduxState: reduxContext.store.getState(),
    }),
    [reduxContext.store, getAllAttributes]
  );
};

export interface Providers {
  trackingAttributes: GlobalTrackingAttributes;
  reduxState: HotelsState;
}
